import { useTranslation } from 'react-i18next'
import { Loader2, Power } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Workflow, WorkflowStatus } from '@/schemas/workflow'

interface StatusConsoleProps {
  workflow: Workflow
  saving: boolean
  onToggle: () => void
}

interface StateStyle {
  accent: string // hex/hsl for glow + stamp
  glow: string
  label: string
  caption: string
}

const STATE_STYLES: Record<WorkflowStatus, StateStyle> = {
  active: {
    accent: '52 211 153',
    glow: 'rgba(52, 211, 153, 0.35)',
    label: 'ATIVO',
    caption: 'em produção',
  },
  draft: {
    accent: '156 163 175',
    glow: 'rgba(156, 163, 175, 0.25)',
    label: 'RASCUNHO',
    caption: 'editável, não publicado',
  },
  inactive: {
    accent: '244 114 182',
    glow: 'rgba(244, 114, 182, 0.3)',
    label: 'INATIVO',
    caption: 'despublicado',
  },
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .format(d)
      .replace(/\.$/, '')
  } catch {
    return iso
  }
}

export function StatusConsole({
  workflow,
  saving,
  onToggle,
}: StatusConsoleProps) {
  const { t } = useTranslation('workflows')
  const style = STATE_STYLES[workflow.status]
  const isActive = workflow.status === 'active'

  const cells: Array<{ label: string; value: string; mono?: boolean }> = [
    { label: 'Versão', value: `v${workflow.version}`, mono: true },
    { label: 'Padrão', value: workflow.pattern_type },
    { label: 'Criado', value: fmtDate(workflow.created_at) },
    { label: 'Atualizado', value: fmtDate(workflow.updated_at) },
  ]

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-border glass-strong"
      style={{
        background: `radial-gradient(circle at 12% 18%, ${style.glow} 0%, transparent 45%), radial-gradient(circle at 88% 82%, rgba(99,102,241,0.08) 0%, transparent 50%)`,
      }}
      data-testid="status-console"
    >
      {/* Noise overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.7 0'/></filter><rect width='120' height='120' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-0">
        {/* Left: vertical state stamp (stacked glyphs) */}
        <div
          className="relative flex items-center justify-center lg:border-r border-border/60 pt-10 pb-24 overflow-hidden"
          style={{ minHeight: '340px' }}
        >
          <div
            aria-hidden
            className="pointer-events-none select-none font-mono font-black leading-[0.9] text-center"
            style={{
              fontSize: 'clamp(44px, 5.5vw, 72px)',
              color: `rgb(${style.accent})`,
              letterSpacing: '0.02em',
              textShadow: `0 0 40px ${style.glow}`,
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
            }}
          >
            {style.label}
          </div>
          <div className="absolute inset-x-0 bottom-4 flex flex-col items-center gap-1 text-center">
            <span className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70">
              estado atual
            </span>
            <span
              className="flex items-center gap-2 text-xs tabular-nums"
              style={{ color: `rgb(${style.accent})` }}
            >
              {isActive && (
                <span className="relative inline-flex h-2 w-2">
                  <span
                    className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping"
                    style={{ backgroundColor: `rgb(${style.accent})` }}
                  />
                  <span
                    className="relative inline-flex h-2 w-2 rounded-full"
                    style={{ backgroundColor: `rgb(${style.accent})` }}
                  />
                </span>
              )}
              {style.caption}
            </span>
          </div>
        </div>

        {/* Right: meta cells + action */}
        <div className="flex flex-col">
          <dl className="grid grid-cols-2 gap-0">
            {cells.map((c, i) => (
              <div
                key={c.label}
                className={cn(
                  'px-6 py-5 border-border/60',
                  i % 2 === 0 ? 'border-r' : '',
                  i < 2 ? 'border-b' : ''
                )}
              >
                <dt className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground/70 mb-1.5">
                  {c.label}
                </dt>
                <dd
                  className={cn(
                    'text-foreground text-lg',
                    c.mono && 'font-mono tabular-nums tracking-tighter'
                  )}
                >
                  {c.value}
                </dd>
              </div>
            ))}
          </dl>

          {/* Action strip */}
          <div className="border-t border-border/60 px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground/70">
                {t('detail.statusToggle.label')}
              </span>
              <span className="text-sm text-muted-foreground/90">
                {isActive
                  ? 'Desativar remove este workflow da API de execução.'
                  : 'Ativar publica o workflow para consumo via API.'}
              </span>
            </div>
            <Button
              variant={isActive ? 'outline' : 'action-emerald'}
              onClick={onToggle}
              disabled={saving}
              className={cn(
                'gap-2 shrink-0',
                isActive && 'border-border/70 text-muted-foreground hover:text-foreground'
              )}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Power className="w-4 h-4" />
              )}
              {saving
                ? '…'
                : isActive
                  ? t('detail.statusToggle.deactivate')
                  : t('detail.statusToggle.activate')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
